import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FileText, File, Image, Plus, Trash2, Search, Loader2, CheckCircle, XCircle, X, FolderOpen, ChevronRight, ChevronDown, Folder, Film, Music, Edit3, Save, Send, HardDrive, RefreshCw, Sparkles, Cloud, MessageSquare, Unplug, ExternalLink } from 'lucide-react';
import { RagTool } from '../types';
import { CaseFolder, ResourceFile } from '../types/library';
import { useTranslation } from '../hooks/useTranslation';
import { ResourceApiService, KnowledgeApiService } from '../services/api';
import {
  FileNode,
  AiIndex,
  openFolder,
  scanDirectory,
  readFileContent,
  writeFileContent,
  renameFile as fsRenameFile,
  restoreDirectoryHandle,
  isFileSystemSupported,
  isTextFile,
  getFileEmoji,
  buildFileTreeListing,
} from '../hooks/useFileSystem';

// 上传状态类型
export interface UploadState {
  isUploading: boolean;
  fileName?: string;
  progress?: number;
  status?: 'uploading' | 'processing' | 'success' | 'error';
  error?: string;
}

interface RagToolsPanelProps {
  ragTools: RagTool[];
  onToolRemove: (toolId: string) => void;
  onAddTool: () => void;
  onFileUpload?: (files: File[]) => void;
  currentLanguage: string;
  isKnowledgeSearchEnabled?: boolean;
  onKnowledgeSearchToggle?: (enabled: boolean) => void;
  onClose?: () => void;
  uploadState?: UploadState | null;
  isLoading?: boolean;
  onLibraryOpen?: () => void;
  caseFolders?: CaseFolder[];
  onCaseFoldersChange?: (folders: CaseFolder[]) => void;
  onLocalFileSelect?: (fileContent: string, fileName: string) => void;
  onAiAnalyze?: (fileTreeListing: string) => void;
  onLocalDirHandleChange?: (handle: FileSystemDirectoryHandle | null) => void;
  onLocalFileTreeChange?: (treeText: string) => void;
  aiIndex?: AiIndex | null;

  // 云存储
  cloudProviders?: { source: string; displayName: string; connected: boolean }[];
  onCloudConnect?: (source: string) => void;
  onCloudDisconnect?: (source: string) => void;
  onCloudRefresh?: (source: string) => void;
  cloudFiles?: { source: string; files: { id: string; name: string; parentName: string; isFolder?: boolean }[]; error?: string; currentFolderId?: string; pathHistory?: {id: string; name: string}[]; isLoading?: boolean }[];

  // 邮件
  emailProviders?: { source: string; displayName: string; connected: boolean }[];
  emailMessages?: { source: string; emails: { id: string; subject: string; from: string; date: string; preview: string; unread: boolean }[]; error?: string; hasMore?: boolean; loadingMore?: boolean }[];
  onEmailConnect?: (source: string) => void;
  onEmailDisconnect?: (source: string) => void;
  onEmailRefresh?: (source: string) => void;
  onEmailLoadMore?: (source: string) => void;

  // 添加的新属性
  onCloudFolderClick?: (source: string, folderId: string, folderName: string) => void;
  onCloudFolderBack?: (source: string) => void;
  onResourceSelect?: (source: string, id: string, name: string, folderName: string) => void;
  onEmailRightClick?: (source: string, id: string, subject: string) => void;
  onFilePreview?: (file: { name: string; data: File | Blob | string; source?: string }) => void;
}

const RagToolsPanel: React.FC<RagToolsPanelProps> = ({
  ragTools,
  onToolRemove,
  onAddTool,
  onFileUpload,
  currentLanguage,
  onClose,
  uploadState,
  isLoading = false,
  onLibraryOpen,
  caseFolders = [],
  onCaseFoldersChange,
  onLocalFileSelect,
  onAiAnalyze,
  onLocalDirHandleChange,
  onLocalFileTreeChange,
  aiIndex,
  cloudProviders = [],
  onCloudConnect,
  onCloudDisconnect,
  onCloudRefresh,
  cloudFiles = [],
  emailProviders = [],
  emailMessages = [],
  onEmailConnect,
  onEmailDisconnect,
  onEmailRefresh,
  onEmailLoadMore,
  onCloudFolderClick,
  onCloudFolderBack,
  onResourceSelect,
  onEmailRightClick,
  onFilePreview,
}) => {
  const [expandedFolderId, setExpandedFolderId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation(currentLanguage);

  // ─── Local Folder State ────────────────────
  const [localDirHandle, setLocalDirHandle] = useState<FileSystemDirectoryHandle | null>(null);
  const [localFileTree, setLocalFileTree] = useState<FileNode[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [expandedLocalPaths, setExpandedLocalPaths] = useState<Set<string>>(new Set());
  const [selectedLocalFile, setSelectedLocalFile] = useState<{
    handle: FileSystemFileHandle;
    name: string;
    path: string;
    parentHandle?: FileSystemDirectoryHandle;
  } | null>(null);
  const [fileContent, setFileContent] = useState<string>('');
  const [editedContent, setEditedContent] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [localFolderExpanded, setLocalFolderExpanded] = useState(true);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Tab 导航状态
  type TabId = 'local' | 'case' | 'gdrive' | 'onedrive' | 'slack' | 'outlook' | 'gmail';
  const [activeTab, setActiveTab] = useState<TabId>('local');

  const isZh = currentLanguage.startsWith('zh');

  // Try to restore last used directory on mount
  useEffect(() => {
    if (isFileSystemSupported()) {
      restoreDirectoryHandle().then(async (handle) => {
        if (handle) {
          try {
            // Verify permission is still valid
            // @ts-ignore
            const perm = await handle.queryPermission({ mode: 'readwrite' });
            if (perm === 'granted') {
              setLocalDirHandle(handle);
              if (onLocalDirHandleChange) onLocalDirHandleChange(handle); // Sync to parant
              setIsScanning(true);
              const tree = await scanDirectory(handle);
              setLocalFileTree(tree);
              if (onLocalFileTreeChange) onLocalFileTreeChange(buildFileTreeListing(tree));
              setIsScanning(false);
            }
          } catch {
            // Permission expired, user will need to re-select
          }
        }
      });
    }
  }, []);

  const handleSelectFolder = useCallback(async () => {
    const handle = await openFolder();
    if (handle) {
      setLocalDirHandle(handle);
      if (onLocalDirHandleChange) onLocalDirHandleChange(handle); // Sync to parant
      setIsScanning(true);
      setSelectedLocalFile(null);
      setFileContent('');
      setEditedContent('');
      try {
        const tree = await scanDirectory(handle);
        setLocalFileTree(tree);
        const treeListing = buildFileTreeListing(tree);
        if (onLocalFileTreeChange) onLocalFileTreeChange(treeListing);
      } catch (err) {
        console.error('[LocalFolder] scan error:', err);
      }
      setIsScanning(false);
    }
  }, []);

  const handleRescan = useCallback(async () => {
    if (!localDirHandle) return;
    setIsScanning(true);
    try {
      const tree = await scanDirectory(localDirHandle);
      setLocalFileTree(tree);
      const treeListing = buildFileTreeListing(tree);
      if (onLocalFileTreeChange) onLocalFileTreeChange(treeListing);
    } catch (err) {
      console.error('[LocalFolder] rescan error:', err);
    }
    setIsScanning(false);
  }, [localDirHandle]);

  const toggleLocalPath = useCallback((path: string) => {
    setExpandedLocalPaths(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  // Find parent directory handle for a file path
  const findParentHandle = useCallback(async (filePath: string): Promise<FileSystemDirectoryHandle | null> => {
    if (!localDirHandle) return null;
    const parts = filePath.split('/');
    parts.pop(); // Remove file name
    let current = localDirHandle;
    for (const part of parts) {
      try {
        current = await current.getDirectoryHandle(part);
      } catch {
        return null;
      }
    }
    return current;
  }, [localDirHandle]);

  const handleLocalFileClick = useCallback(async (node: FileNode) => {
    if (node.kind !== 'file') return;

    // Toggle: 再次点击同一文件 → 收起 AI 标签
    if (selectedLocalFile?.path === node.path) {
      setSelectedLocalFile(null);
      return;
    }

    const handle = node.handle as FileSystemFileHandle;
    const parentHandle = await findParentHandle(node.path);
    setSelectedLocalFile({
      handle,
      name: node.name,
      path: node.path,
      parentHandle: parentHandle || undefined,
    });

    // Trigger file preview in the main area
    if (onFilePreview) {
      try {
        const file = await handle.getFile();
        onFilePreview({ name: node.name, data: file, source: 'Local' });
      } catch (err) {
        console.error('[RagToolsPanel] Failed to read file for preview:', err);
      }
    }
  }, [findParentHandle, selectedLocalFile, onFilePreview]);

  const handleSaveFile = useCallback(async () => {
    if (!selectedLocalFile || !isTextFile(selectedLocalFile.name)) return;
    setIsSaving(true);
    try {
      await writeFileContent(selectedLocalFile.handle, editedContent);
      setFileContent(editedContent);
      setIsEditing(false);
      setSaveMessage(t('fileSaved'));
      setTimeout(() => setSaveMessage(null), 2000);
    } catch (err) {
      console.error('[LocalFolder] save error:', err);
      setSaveMessage('Save failed');
      setTimeout(() => setSaveMessage(null), 3000);
    }
    setIsSaving(false);
  }, [selectedLocalFile, editedContent, t]);

  const handleRenameFile = useCallback(async () => {
    if (!selectedLocalFile || !selectedLocalFile.parentHandle || !renameValue.trim()) return;
    const result = await fsRenameFile(selectedLocalFile.parentHandle, selectedLocalFile.name, renameValue.trim());
    if (result) {
      setSelectedLocalFile(prev => prev ? { ...prev, handle: result, name: renameValue.trim() } : null);
      setIsRenaming(false);
      setSaveMessage(t('fileRenamed').replace('%{name}', renameValue.trim()));
      setTimeout(() => setSaveMessage(null), 2000);
      await handleRescan();
    }
  }, [selectedLocalFile, renameValue, t, handleRescan]);

  const handleAddToChat = useCallback(() => {
    if (!selectedLocalFile || !fileContent || !onLocalFileSelect) return;
    const content = isEditing ? editedContent : fileContent;
    onLocalFileSelect(content, selectedLocalFile.name);
    setSaveMessage(isZh ? '已添加到对话' : 'Added to chat');
    setTimeout(() => setSaveMessage(null), 2000);
  }, [selectedLocalFile, fileContent, editedContent, isEditing, onLocalFileSelect, isZh]);

  // ─── Existing Functions ────────────────────

  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0 && onFileUpload) {
      onFileUpload(files);
    }
    if (e.target) {
      e.target.value = '';
    }
  };

  const getFileIcon = (type: ResourceFile['type']) => {
    switch (type) {
      case 'video': return <Film size={13} className="text-purple-500 flex-shrink-0" />;
      case 'audio': return <Music size={13} className="text-green-500 flex-shrink-0" />;
      case 'document': return <FileText size={13} className="text-blue-500 flex-shrink-0" />;
      case 'image': return <Image size={13} className="text-orange-500 flex-shrink-0" />;
      default: return <File size={13} className="text-gray-400 flex-shrink-0" />;
    }
  };

  const toggleFolderEnabled = (folderId: string) => {
    if (!onCaseFoldersChange) return;
    const folder = caseFolders.find(f => f.id === folderId);
    if (!folder) return;
    const newEnabled = !folder.enabled;
    // 乐观更新
    onCaseFoldersChange(caseFolders.map(f =>
      f.id === folderId ? { ...f, enabled: newEnabled } : f
    ));
    // 持久化（fire-and-forget，失败回滚）
    ResourceApiService.updateFolder(folderId, { enabled: newEnabled }).catch(() => {
      onCaseFoldersChange(caseFolders.map(f =>
        f.id === folderId ? { ...f, enabled: !newEnabled } : f
      ));
    });
  };

  const getUploadStatusText = () => {
    if (!uploadState) return '';
    const name = uploadState.fileName || 'file';
    switch (uploadState.status) {
      case 'uploading': return t('uploading').replace('%{fileName}', name);
      case 'processing': return t('processing').replace('%{fileName}', name);
      case 'success': return t('uploadSuccess').replace('%{fileName}', name);
      case 'error': return t('uploadError').replace('%{error}', uploadState.error || t('unknownError'));
      default: return '';
    }
  };

  const enabledCount = caseFolders.filter(f => f.enabled).length;

  // ─── Render File Tree ────────────────────
  const renderFileTree = (nodes: FileNode[], depth: number = 0) => {
    return nodes.map(node => {
      const isExpanded = expandedLocalPaths.has(node.path);
      const isSelected = selectedLocalFile?.path === node.path;

      if (node.kind === 'directory') {
        return (
          <div key={node.path}>
            <button
              onClick={() => toggleLocalPath(node.path)}
              draggable
              onDragStart={(e) => {
                e.stopPropagation();
                e.dataTransfer.setData('application/json', JSON.stringify({
                  type: 'mention',
                  item: { id: node.path, source: 'local', folderName: 'Local', name: `📁 ${node.name}` }
                }));
              }}
              className={`w-full flex items-center gap-1.5 py-1 hover:bg-gray-100 rounded transition-colors text-left`}
              style={{ paddingLeft: `${depth * 12 + 8}px` }}
            >
              {isExpanded
                ? <ChevronDown size={12} className="text-gray-400 flex-shrink-0" />
                : <ChevronRight size={12} className="text-gray-400 flex-shrink-0" />
              }
              <Folder size={13} className="text-amber-500 flex-shrink-0" />
              <span className="text-xs text-gray-700 truncate">{node.name}</span>
            </button>
            {isExpanded && node.children && (
              <div>{renderFileTree(node.children, depth + 1)}</div>
            )}
          </div>
        );
      }

      return (
        <button
          key={node.path}
          onClick={() => handleLocalFileClick(node)}
          draggable
          onDragStart={(e) => {
            e.stopPropagation();
            e.dataTransfer.setData('text/plain', `@[Local/${node.name}]`);
            e.dataTransfer.setData('application/json', JSON.stringify({
              type: 'mention',
              item: { id: node.path, source: 'local', folderName: 'Local', name: node.name }
            }));
          }}
          className={`w-full flex items-center gap-1.5 py-1 rounded transition-colors text-left ${isSelected ? 'bg-gray-100 ring-1 ring-gray-300' : 'hover:bg-gray-50'
            }`}
          style={{ paddingLeft: `${depth * 12 + 22}px` }}
        >
          <span className="text-xs flex-shrink-0">{getFileEmoji(node.name)}</span>
          <span className={`text-xs truncate ${isSelected ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
            {node.name}
          </span>
        </button>
      );
    });
  };

  return (
    <div className="w-full flex flex-col h-full overflow-hidden">
      {/* 标题栏 */}
      <div className="py-[10px] px-4 border-b border-gray-200">
        <div className="flex items-center justify-between h-7">
          <h2 className="text-base font-semibold text-gray-900">{t('resources')}</h2>
          <div className="flex items-center gap-2">
            {onClose && (
              <button
                onClick={onClose}
                className="w-7 h-7 flex items-center justify-center rounded border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 transition-colors"
                title={t('closeResourcesPanel')}
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 上传进度指示器 */}
      {uploadState && (uploadState.isUploading || uploadState.status === 'success' || uploadState.status === 'error') && (
        <div className={`mx-4 mt-3 p-3 rounded-lg border ${uploadState.status === 'error'
          ? 'bg-red-50 border-red-200'
          : uploadState.status === 'success'
            ? 'bg-green-50 border-green-200'
            : 'bg-gray-50 border-gray-200'
          }`}>
          <div className="flex items-center gap-2">
            {uploadState.status === 'success' ? (
              <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
            ) : uploadState.status === 'error' ? (
              <XCircle size={16} className="text-red-500 flex-shrink-0" />
            ) : (
              <Loader2 size={16} className="text-gray-600 animate-spin flex-shrink-0" />
            )}
            <span className={`text-sm ${uploadState.status === 'error' ? 'text-red-700' : uploadState.status === 'success' ? 'text-green-700' : 'text-gray-800'}`}>
              {getUploadStatusText()}
            </span>
          </div>
          {uploadState.isUploading && uploadState.progress !== undefined && (
            <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
              <div className="bg-gray-500 h-1.5 rounded-full transition-all duration-300" style={{ width: `${uploadState.progress}%` }} />
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════
          品牌 Tab 导航栏
          ═══════════════════════════════════════════ */}
      <div className="px-3 pt-3 pb-1">
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
          {[
            { id: 'local' as TabId, label: isZh ? '本地' : 'Local', icon: <HardDrive size={16} />, color: 'text-blue-600 bg-blue-50 border-blue-200' },
            { id: 'case' as TabId, label: isZh ? '案件' : 'Cases', icon: <FolderOpen size={16} />, color: 'text-amber-600 bg-amber-50 border-amber-200' },
            { id: 'gdrive' as TabId, label: 'Drive', icon: <svg viewBox="0 0 87.3 78" width="16" height="16"><path d="M6.6 66.85L29.3 78l20.6-35.7L27.2 6.5z" fill="#0066DA"/><path d="M58.2 78l22.7-11.15L58.2 31.3 37.6 42.3z" fill="#00AC47"/><path d="M27.2 6.5L6.6 42.3h41.3L58.2 6.5z" fill="#EA4335"/><path d="M48 42.3H6.6l22.7 24.55L48 42.3z" fill="#00832D"/><path d="M58.2 6.5L27.2 6.5l20.7 35.8L58.2 6.5z" fill="#2684FC"/><path d="M80.9 66.85L58.2 31.3l-10.3 11L80.9 66.85z" fill="#FFBA00"/></svg>, color: 'text-green-600 bg-green-50 border-green-200' },
            { id: 'onedrive' as TabId, label: 'OneDrive', icon: <svg viewBox="0 0 24 24" width="16" height="16"><path d="M12.5 10c-.3-2.8-2.7-5-5.5-5C4.6 5 2.4 6.7 1.7 9.1 .7 9.5 0 10.5 0 11.7 0 13.2 1.2 14.5 2.7 14.5H11l1.5-4.5z" fill="#0364B8"/><path d="M12.5 10l-.3-.1c-.4-2-2-3.5-3.9-3.9 1-1.2 2.5-2 4.2-2 2.5 0 4.6 1.7 5.2 4l.3 0c2.2 0 4 1.8 4 4s-1.8 4-4 4H13l-.5-6z" fill="#0078D4"/><path d="M6.3 10c-.1.3-.2.6-.2.9C6 12.6 7.4 14 9 14h4l-1.5-4.5-.7-.5H6.3z" fill="#1490DF"/></svg>, color: 'text-sky-600 bg-sky-50 border-sky-200' },
            { id: 'slack' as TabId, label: 'Slack', icon: <svg viewBox="0 0 16 16" width="16" height="16"><path d="M3.4 10.1c0 .9-.8 1.7-1.7 1.7S0 11 0 10.1s.8-1.7 1.7-1.7h1.7zm.8 0c0-.9.8-1.7 1.7-1.7s1.7.8 1.7 1.7v4.2c0 .9-.8 1.7-1.7 1.7s-1.7-.8-1.7-1.7z" fill="#E01E5A"/><path d="M5.9 3.4c-.9 0-1.7-.8-1.7-1.7S5 0 5.9 0s1.7.8 1.7 1.7v1.7zm0 .8c.9 0 1.7.8 1.7 1.7s-.8 1.7-1.7 1.7H1.7C.8 7.6 0 6.8 0 5.9s.8-1.7 1.7-1.7z" fill="#36C5F0"/><path d="M12.6 5.9c0-.9.8-1.7 1.7-1.7s1.7.8 1.7 1.7-.8 1.7-1.7 1.7h-1.7zm-.8 0c0 .9-.8 1.7-1.7 1.7S8.4 6.8 8.4 5.9V1.7C8.4.8 9.2 0 10.1 0s1.7.8 1.7 1.7z" fill="#2EB67D"/><path d="M10.1 12.6c.9 0 1.7.8 1.7 1.7s-.8 1.7-1.7 1.7-1.7-.8-1.7-1.7v-1.7zm0-.8c-.9 0-1.7-.8-1.7-1.7s.8-1.7 1.7-1.7h4.2c.9 0 1.7.8 1.7 1.7s-.8 1.7-1.7 1.7z" fill="#ECB22E"/></svg>, color: 'text-purple-600 bg-purple-50 border-purple-200' },
            { id: 'outlook' as TabId, label: 'Outlook', icon: <svg viewBox="0 0 24 24" width="16" height="16"><path d="M12 2L2 7v10l10 5 10-5V7z" fill="#0078D4"/><ellipse cx="8" cy="12.5" rx="4" ry="5" fill="#fff" opacity=".9"/><path d="M8 9c-1.7 0-3 1.6-3 3.5S6.3 16 8 16s3-1.6 3-3.5S9.7 9 8 9zm0 1.2c1 0 1.8 1 1.8 2.3s-.8 2.3-1.8 2.3-1.8-1-1.8-2.3.8-2.3 1.8-2.3z" fill="#0078D4"/><path d="M14 8v8l8-4z" fill="#fff" opacity=".6"/></svg>, color: 'text-blue-600 bg-blue-50 border-blue-200' },
            { id: 'gmail' as TabId, label: 'Gmail', icon: <svg viewBox="0 0 24 24" width="16" height="16"><path d="M2 6l10 7 10-7v12H2z" fill="#F6F6F6" stroke="#E0E0E0" strokeWidth=".5"/><path d="M22 6L12 13 2 6V4l10 7 10-7z" fill="#EA4335"/><path d="M2 6v12h3V9.5L12 15l7-5.5V18h3V6L12 13z" fill="none"/><path d="M2 6l3 2.25V18H2z" fill="#C5221F"/><path d="M22 6l-3 2.25V18h3z" fill="#C5221F"/></svg>, color: 'text-red-600 bg-red-50 border-red-200' },
          ].map(tab => {
            const isActive = activeTab === tab.id;
            const isConnected = tab.id === 'local' || tab.id === 'case'
              || cloudProviders.find(p => p.source === tab.id)?.connected
              || emailProviders.find(p => p.source === tab.id)?.connected;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                title={tab.label}
                className={`relative flex items-center justify-center w-8 h-8 rounded-lg border transition-all ${
                  isActive
                    ? `${tab.color} shadow-sm`
                    : 'text-gray-500 bg-white border-gray-200 hover:bg-gray-50'
                } ${!isConnected && tab.id !== 'local' && tab.id !== 'case' ? 'opacity-60' : ''}`}
              >
                {tab.icon}
                {!isConnected && tab.id !== 'local' && tab.id !== 'case' && (
                  <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-gray-300" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* 主要内容区域 */}
      <div className="flex-1 overflow-y-auto">

        {/* ═══════════════════════════════════════════
            本地文件夹区域 (Local Folders)
            ═══════════════════════════════════════════ */}
        {activeTab === 'local' && <div className="px-4 pt-4 pb-2">
          <div className="mb-3">
            {/* Row 1: Section title */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => setLocalFolderExpanded(!localFolderExpanded)}
                className="flex items-center gap-2"
              >
                {localFolderExpanded
                  ? <ChevronDown size={12} className="text-gray-400" />
                  : <ChevronRight size={12} className="text-gray-400" />
                }
                <HardDrive size={13} className="text-gray-500" />
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t('localFolders')}
                </span>
              </button>
              {/* Compact action buttons */}
              <div className="flex items-center gap-1">
                {localDirHandle && localFileTree.length > 0 && (
                  <button
                    onClick={() => {
                      const listing = buildFileTreeListing(localFileTree);
                      onAiAnalyze?.(listing);
                    }}
                    disabled={isAnalyzing}
                    className={`flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded transition-colors ${isAnalyzing
                      ? 'bg-purple-50 text-purple-400 cursor-not-allowed'
                      : 'bg-purple-50 text-purple-600 hover:bg-purple-100'
                      }`}
                    title={t('aiAnalyze')}
                  >
                    {isAnalyzing ? (
                      <Loader2 size={10} className="animate-spin" />
                    ) : (
                      <Sparkles size={10} />
                    )}
                    {t('aiAnalyze')}
                  </button>
                )}
                {localDirHandle && (
                  <button
                    onClick={handleRescan}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                    title={isZh ? '刷新' : 'Refresh'}
                  >
                    <RefreshCw size={12} className={`text-gray-400 ${isScanning ? 'animate-spin' : ''}`} />
                  </button>
                )}
                <button
                  onClick={handleSelectFolder}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                  title={localDirHandle ? t('reselectFolder') : (isZh ? '选择文件夹' : 'Select Folder')}
                >
                  <FolderOpen size={12} className="text-gray-400" />
                </button>
              </div>
            </div>
          </div>

          {localFolderExpanded && (
            <>
              {!isFileSystemSupported() ? (
                <div className="text-xs text-red-500 p-3 bg-red-50 rounded-lg border border-red-200">
                  {t('browserNotSupported')}
                </div>
              ) : !localDirHandle ? (
                /* 未选择文件夹 — 空状态 */
                <button
                  onClick={handleSelectFolder}
                  className="w-full flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 border border-dashed border-gray-300 rounded-xl transition-colors text-left group"
                >
                  <div className="p-2 bg-white rounded-lg border border-gray-200 group-hover:border-gray-300 transition-colors">
                    <HardDrive size={18} className="text-gray-400" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-600">
                      {t('selectLocalFolder')}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {t('noFolderSelected')}
                    </div>
                  </div>
                </button>
              ) : isScanning ? (
                <div className="flex items-center gap-2 p-3">
                  <Loader2 size={14} className="animate-spin text-gray-500" />
                  <span className="text-xs text-gray-500">{t('scanningFolder')}</span>
                </div>
              ) : (
                /* 文件树 + 预览/编辑区 */
                <div className="space-y-2">
                  {/* 当前文件夹名 */}
                  <div className="text-xs text-gray-400 px-1 truncate flex items-center gap-1">
                    <Folder size={11} className="flex-shrink-0" />
                    {localDirHandle.name}
                  </div>

                  {/* 文件树 */}
                  <div className="max-h-48 overflow-y-auto border border-gray-100 rounded-lg p-1 bg-white">
                    {localFileTree.length === 0 ? (
                      <div className="text-xs text-gray-400 text-center py-3 italic">
                        {isZh ? '空文件夹' : 'Empty folder'}
                      </div>
                    ) : (
                      renderFileTree(localFileTree)
                    )}
                  </div>

                  {/* AI 标签面板 */}
                  {selectedLocalFile && (() => {
                    const tag = aiIndex?.files?.[selectedLocalFile.path];
                    return tag ? (
                      <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-100">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="text-xs">{getFileEmoji(selectedLocalFile.name)}</span>
                            <span className="text-xs font-medium text-gray-700 truncate">{selectedLocalFile.name}</span>
                            <Edit3 size={12} className="text-gray-300 flex-shrink-0" />
                          </div>
                        </div>
                        <div className="p-3 space-y-2">
                          <div className="flex items-center gap-1.5">
                            <Sparkles size={11} className="text-purple-500 flex-shrink-0" />
                            <span className="text-xs font-semibold text-gray-800">{tag.title}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 text-[10px] rounded-md font-medium border border-purple-100">
                              {tag.type}
                            </span>
                          </div>
                          <div className="text-[11px] text-gray-500 leading-relaxed">{tag.summary}</div>
                          {tag.entities.length > 0 && (
                            <div className="flex flex-wrap gap-1 pt-0.5">
                              {tag.entities.map((entity, i) => (
                                <span key={i} className="px-1.5 py-0.5 bg-gray-50 border border-gray-200 text-[10px] text-gray-600 rounded-md">
                                  {entity}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-100">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="text-xs">{getFileEmoji(selectedLocalFile.name)}</span>
                            <span className="text-xs font-medium text-gray-700 truncate">{selectedLocalFile.name}</span>
                          </div>
                        </div>
                        <div className="p-3">
                          <div className="text-[11px] text-gray-400 italic">
                            {isZh ? '暂无 AI 标签，请点击「AI 分析」生成' : 'No AI tags yet. Click "AI Analyze" to generate.'}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </>
          )}
        </div>}

        {/* 分隔线 — 已移除，改用 tab 切换 */}

        {/* ═══════════════════════════════════════════
            案件文件夹区域 (Case Folders)
            ═══════════════════════════════════════════ */}
        {activeTab === 'case' && <div className="px-4 pt-4 pb-2">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {isZh ? '案件文件夹' : 'Case Folders'}
              </span>
              {enabledCount > 0 && (
                <span className="text-xs bg-gray-900 text-white px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                  {enabledCount}
                </span>
              )}
            </div>
            {onLibraryOpen && (
              <button
                onClick={onLibraryOpen}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                <Plus size={12} />
                {isZh ? '管理' : 'Manage'}
              </button>
            )}
          </div>

          {caseFolders.length === 0 ? (
            /* 空状态 */
            <button
              onClick={onLibraryOpen}
              className="w-full flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 border border-dashed border-gray-300 rounded-xl transition-colors text-left group"
            >
              <div className="p-2 bg-white rounded-lg border border-gray-200 group-hover:border-gray-300 transition-colors">
                <FolderOpen size={18} className="text-gray-400" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-600">
                  {isZh ? '创建案件文件夹' : 'Create Case Folder'}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {isZh ? '上传案件资料，在对话中使用' : 'Upload case files to use in chats'}
                </div>
              </div>
            </button>
          ) : (
            /* 文件夹列表 */
            <div className="space-y-1">
              {caseFolders.map(folder => (
                <div key={folder.id} className="rounded-xl border border-gray-100 overflow-hidden">
                  {/* 文件夹行 */}
                  <div className={`flex items-center gap-2 px-3 py-2.5 transition-colors ${folder.enabled ? 'bg-gray-50' : 'bg-white'
                    }`}>
                    {/* 展开/收起箭头 */}
                    <button
                      onClick={() => setExpandedFolderId(expandedFolderId === folder.id ? null : folder.id)}
                      className="p-0.5 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
                    >
                      {expandedFolderId === folder.id
                        ? <ChevronDown size={14} className="text-gray-400" />
                        : <ChevronRight size={14} className="text-gray-400" />
                      }
                    </button>

                    {/* 文件夹图标 + 名字 */}
                    <button
                      onClick={() => setExpandedFolderId(expandedFolderId === folder.id ? null : folder.id)}
                      draggable
                      onDragStart={(e) => {
                        e.stopPropagation();
                        e.dataTransfer.setData('application/json', JSON.stringify({
                          type: 'mention',
                          item: { id: folder.id, source: 'case', folderName: 'Case', name: `📁 ${folder.name}` }
                        }));
                      }}
                      className="flex items-center gap-2 flex-1 min-w-0 text-left"
                    >
                      <Folder size={15} className={`flex-shrink-0 ${folder.enabled ? 'text-gray-700' : 'text-gray-300'}`} />
                      <div className="min-w-0 flex-1">
                        <div className={`text-sm font-medium truncate ${folder.enabled ? 'text-gray-800' : 'text-gray-400'}`}>
                          {folder.name}
                        </div>
                        <div className="text-xs text-gray-400">
                          {folder.files.length} {isZh ? '个文件' : 'files'}
                        </div>
                      </div>
                    </button>

                    {/* 启用/禁用开关 */}
                    <button
                      onClick={() => toggleFolderEnabled(folder.id)}
                      className="flex-shrink-0 focus:outline-none ml-1"
                      title={folder.enabled
                        ? (isZh ? '已启用 · 点击禁用' : 'Enabled · Click to disable')
                        : (isZh ? '已禁用 · 点击启用' : 'Disabled · Click to enable')
                      }
                    >
                      <div className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${folder.enabled ? 'bg-[#1A1A1A]' : 'bg-gray-200'}`}>
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${folder.enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </div>
                    </button>
                  </div>

                  {/* 展开的文件列表 */}
                  {expandedFolderId === folder.id && (
                    <div className="border-t border-gray-100 bg-white">
                      {folder.files.length === 0 ? (
                        <div className="px-4 py-3 text-xs text-gray-400 text-center italic">
                          {isZh ? '暂无文件 · 在资料库中上传' : 'No files · Upload in Library'}
                        </div>
                      ) : (
                        <div className="py-1">
                          {folder.files.map(file => (
                            <div key={file.id} 
                                 className="flex items-center gap-2.5 px-4 py-1.5 hover:bg-gray-50 transition-colors cursor-pointer"
                                 draggable
                                 onDragStart={(e) => {
                                   e.stopPropagation();
                                   e.dataTransfer.setData('text/plain', `@[${folder.name}/${file.name}]`);
                                   e.dataTransfer.setData('application/json', JSON.stringify({
                                     type: 'mention',
                                     item: { id: file.id, source: 'case', folderName: folder.name, name: file.name }
                                   }));
                                 }}
                                 onDoubleClick={async () => {
                                   if (!onFilePreview) return;
                                   try {
                                     const blob = await ResourceApiService.downloadFile(file.id);
                                     onFilePreview({ name: file.name, data: blob, source: folder.name });
                                   } catch (err) {
                                     console.error('[RagPanel] Failed to download file for preview:', err);
                                   }
                                 }}
                            >
                              {getFileIcon(file.type)}
                              <span className="text-xs text-gray-600 truncate flex-1">{file.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>}

        {/* ═══════════════════════════════════════════
            云存储连接区域 (Cloud Storage) — 在对应 tab 下显示
            ═══════════════════════════════════════════ */}
        {/* 云存储 Tab 内容 — 每个 provider 对应自己的 tab */}
        {(['gdrive', 'onedrive', 'slack'] as TabId[]).map(tabId => {
          if (activeTab !== tabId) return null;
          const provider = cloudProviders.find(p => p.source === tabId);
          const displayName = tabId === 'gdrive' ? 'Google Drive' : tabId === 'onedrive' ? 'OneDrive' : 'Slack';
          const isConnected = provider?.connected || false;
          const cloudEntry = cloudFiles.find(cf => cf.source === tabId);
          const providerFileList = cloudEntry?.files || [];
          const cloudError = cloudEntry?.error;

          return (
            <div key={tabId} className="px-4 pt-4 pb-2">
              {!isConnected ? (
                /* 未连接状态 */
                <div className="flex flex-col items-center gap-3 py-8 text-center">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                    <Cloud size={24} className="text-gray-400" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700">{isZh ? `连接 ${displayName}` : `Connect ${displayName}`}</div>
                    <div className="text-xs text-gray-400 mt-1">{isZh ? '授权后可直接在对话中 @ 引用文件' : 'Authorize to @-mention files in chat'}</div>
                  </div>
                  <button
                    onClick={() => onCloudConnect?.(tabId)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <ExternalLink size={14} />
                    {isZh ? '连接账号' : 'Connect Account'}
                  </button>
                </div>
              ) : (
                /* 已连接 — 文件列表 */
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-gray-400">
                      {cloudError ? (isZh ? '加载失败' : 'Load failed') : `${providerFileList.length} ${isZh ? '个项目' : 'items'}`}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onCloudRefresh?.(tabId)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title={isZh ? '刷新' : 'Refresh'}
                      >
                        <RefreshCw size={11} className="text-gray-400" />
                      </button>
                      <button
                        onClick={() => onCloudDisconnect?.(tabId)}
                        className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Unplug size={10} />
                        {isZh ? '断开' : 'Disconnect'}
                      </button>
                    </div>
                  </div>
                  {cloudError ? (
                    <div className="text-center py-6">
                      <div className="text-xs text-red-500 mb-2">{cloudError}</div>
                      <button
                        onClick={() => onCloudRefresh?.(tabId)}
                        className="text-xs text-blue-500 hover:text-blue-600"
                      >{isZh ? '点击重试' : 'Click to retry'}</button>
                    </div>
                  ) : cloudEntry?.isLoading ? (
                    <div className="py-12 flex flex-col items-center justify-center space-y-3">
                      <div className="w-6 h-6 rounded-full border-2 border-indigo-200 border-t-indigo-600 animate-spin"></div>
                      <span className="text-xs text-gray-400">{isZh ? '加载中...' : 'Loading...'}</span>
                    </div>
                  ) : (
                    <div className="space-y-0.5">
                      {/* 返回上级目录按钮 */}
                      {cloudEntry?.currentFolderId && (
                        <button
                          onClick={() => onCloudFolderBack?.(tabId)}
                          className="flex items-center gap-2 px-2 py-1.5 w-full text-left rounded-lg hover:bg-gray-50 transition-colors text-xs text-blue-600 mb-1"
                        >
                          <svg viewBox="0 0 24 24" width="13" height="13" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                          {isZh ? '返回上级' : 'Back to parent'}
                        </button>
                      )}
                      {providerFileList.map(file => {
                        const sourceLabel = tabId === 'gdrive' ? 'GDrive' : tabId === 'onedrive' ? 'OneDrive' : 'Slack';
                        const folderName = file.isFolder ? sourceLabel : `${sourceLabel}/${file.parentName}`;
                        const mentionName = file.isFolder ? `📁 ${file.name}` : file.name;
                        return (
                        <div key={file.id} 
                             className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                             onClick={() => {
                               if (file.isFolder) {
                                  onCloudFolderClick?.(tabId, file.id, file.name);
                               }
                             }}
                             onDoubleClick={async () => {
                               if (file.isFolder || !onFilePreview) return;
                               try {
                                 const blob = await ResourceApiService.downloadCloudFile(tabId, file.id);
                                 onFilePreview({ name: file.name, data: blob, source: tabId === 'gdrive' ? 'Google Drive' : 'OneDrive' });
                               } catch (err) {
                                 console.error('[RagPanel] Failed to download cloud file for preview:', err);
                               }
                             }}
                             draggable
                             onDragStart={(e) => {
                               e.dataTransfer.setData('text/plain', `@[${folderName}/${mentionName}]`);
                               e.dataTransfer.setData('application/json', JSON.stringify({
                                 type: 'mention',
                                 item: { id: file.id, source: tabId, folderName: sourceLabel, name: mentionName }
                               }));
                             }}
                        >
                          {file.isFolder
                            ? <Folder size={13} className="text-amber-500 flex-shrink-0" />
                            : <FileText size={13} className="text-gray-400 flex-shrink-0" />
                          }
                          <div className="min-w-0 flex-1">
                            <div className="text-xs text-gray-700 truncate">{file.name}</div>
                            <div className="text-[10px] text-gray-400 truncate">{file.parentName}</div>
                          </div>
                        </div>
                      )})}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* ═══════════════════════════════════════════
            邮件 Tab 内容 (Outlook / Gmail)
            ═══════════════════════════════════════════ */}
        {(['outlook', 'gmail'] as TabId[]).map(tabId => {
          if (activeTab !== tabId) return null;
          const provider = emailProviders.find(p => p.source === tabId);
          const displayName = tabId === 'outlook' ? 'Outlook' : 'Gmail';
          const isConnected = provider?.connected || false;

          return (
            <div key={tabId} className="px-4 pt-4 pb-2">
              {!isConnected ? (
                <div className="flex flex-col items-center gap-3 py-8 text-center">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                    {tabId === 'outlook'
                      ? <svg viewBox="0 0 24 24" width="24" height="24"><path d="M12 2L2 7v10l10 5 10-5V7z" fill="#0078D4"/><ellipse cx="8" cy="12.5" rx="4" ry="5" fill="#fff" opacity=".9"/></svg>
                      : <svg viewBox="0 0 24 24" width="24" height="24"><path d="M2 6l10 7 10-7v12H2z" fill="#F6F6F6" stroke="#E0E0E0" strokeWidth=".5"/><path d="M22 6L12 13 2 6V4l10 7 10-7z" fill="#EA4335"/></svg>
                    }
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-700">{isZh ? `连接 ${displayName}` : `Connect ${displayName}`}</div>
                    <div className="text-xs text-gray-400 mt-1">{isZh ? '授权后 AI 可搜索、阅读、回复和发送邮件' : 'Authorize AI to search, read, reply and send emails'}</div>
                  </div>
                  <button
                    onClick={() => onEmailConnect?.(tabId)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <ExternalLink size={14} />
                    {isZh ? '连接邮箱' : 'Connect Email'}
                  </button>
                </div>
              ) : (() => {
                const emailEntry = emailMessages.find(em => em.source === tabId);
                const msgs = emailEntry?.emails || [];
                const isLoaded = !!emailEntry; // 区分"未加载"和"加载完但为空"
                const emailError = emailEntry?.error;
                return (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-gray-400">
                      {emailError ? (isZh ? '加载失败' : 'Load failed') : msgs.length > 0 ? `${msgs.filter(e => e.unread).length} ${isZh ? '封未读' : 'unread'} / ${msgs.length} ${isZh ? '封' : 'total'}` : (isZh ? '已连接' : 'Connected')}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onEmailRefresh?.(tabId)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title={isZh ? '刷新' : 'Refresh'}
                      >
                        <RefreshCw size={11} className="text-gray-400" />
                      </button>
                      <button
                        onClick={() => onEmailDisconnect?.(tabId)}
                        className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Unplug size={10} />
                      </button>
                    </div>
                  </div>

                  {emailError ? (
                    <div className="text-center py-6">
                      <div className="text-xs text-red-500 mb-2">{emailError}</div>
                      <button
                        onClick={() => onEmailRefresh?.(tabId)}
                        className="text-xs text-blue-500 hover:text-blue-600"
                      >{isZh ? '点击重试' : 'Click to retry'}</button>
                    </div>
                  ) : msgs.length === 0 ? (
                    <div className="text-center py-6 text-xs text-gray-400">
                      {isLoaded ? (isZh ? '暂无邮件' : 'No emails') : (isZh ? '加载中...' : 'Loading...')}
                    </div>
                  ) : (
                    <div className="space-y-0.5">
                      {msgs.map(email => {
                        const displayName = tabId === 'outlook' ? 'Outlook' : 'Gmail';
                        const shortDate = email.date ? new Date(email.date).toLocaleDateString() : '';
                        const folderName = `${displayName}/${email.from}${shortDate ? ` · ${shortDate}` : ''}`;
                        const mentionName = `📧 ${email.subject}`;
                        return (
                        <div key={email.id} 
                             className={`px-2.5 py-2 rounded-lg transition-colors hover:bg-gray-50 cursor-pointer ${email.unread ? 'bg-blue-50/50' : ''}`}
                             onDoubleClick={() => onEmailRightClick?.(tabId, email.id, email.subject)}
                             onContextMenu={(e) => { e.preventDefault(); onEmailRightClick?.(tabId, email.id, email.subject); }}
                             draggable
                             onDragStart={(e) => {
                               e.dataTransfer.setData('text/plain', `@[${folderName}/${mentionName}]`);
                               e.dataTransfer.setData('application/json', JSON.stringify({
                                 type: 'mention',
                                 item: { id: email.id, source: tabId, name: mentionName, folderName: folderName }
                               }));
                             }}
                        >
                          <div className="flex items-center gap-2 mb-0.5">
                            {email.unread && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />}
                            <span className={`text-xs truncate ${email.unread ? 'font-semibold text-gray-800' : 'text-gray-600'}`}>{email.from}</span>
                            <span className="text-[10px] text-gray-400 ml-auto flex-shrink-0">
                              {email.date ? new Date(email.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
                            </span>
                          </div>
                          <div className={`text-xs truncate ${email.unread ? 'font-medium text-gray-700' : 'text-gray-500'}`}>{email.subject}</div>
                          <div className="text-[10px] text-gray-400 truncate mt-0.5">{email.preview}</div>
                        </div>
                      )})}
                      {/* Load More 按钮 */}
                      {emailEntry?.hasMore && (
                        <button
                          onClick={() => onEmailLoadMore?.(tabId)}
                          disabled={emailEntry?.loadingMore}
                          className={`w-full py-2.5 mt-1 text-xs rounded-lg transition-colors font-medium flex items-center justify-center gap-2 ${emailEntry?.loadingMore ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'}`}
                        >
                          {emailEntry?.loadingMore ? (
                            <>
                              <span className="w-3 h-3 rounded-full border-2 border-gray-300 border-t-blue-500 animate-spin" />
                              {isZh ? '加载中...' : 'Loading...'}
                            </>
                          ) : (
                            isZh ? '加载更多邮件...' : 'Load more emails...'
                          )}
                        </button>
                      )}
                    </div>
                  )}

                  {/* 使用提示 */}
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="text-[10px] text-gray-400">{isZh ? '💡 在聊天中输入 @ 引用邮件，或直接说：' : '💡 Type @ to reference an email, or say:'}</div>
                    <div className="text-[10px] text-gray-400 mt-1">
                      {isZh ? '"搜一下张律师发来的邮件"' : '"Search emails from Zhang"'}
                    </div>
                  </div>
                </div>
                );
              })()}
            </div>
          );
        })}

        {/* 上传的知识库文档 — 在案件 tab 下显示 */}
        {activeTab === 'case' && ragTools.length > 0 && (
          <div className="px-4 pt-3 pb-4 border-t border-gray-100">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">
              {isZh ? '上传文档' : 'Uploaded Docs'}
            </span>
            <div className="space-y-1">
              {ragTools.map((tool) => (
                <div key={tool.id}
                  className="group flex items-center justify-between px-3 py-2 bg-white border border-gray-100 rounded-lg hover:border-gray-200 transition-all cursor-pointer"
                  onDoubleClick={async () => {
                    if (!onFilePreview) return;
                    try {
                      const blob = await KnowledgeApiService.downloadDocument(tool.id);
                      onFilePreview({ name: tool.name, data: blob, source: isZh ? '知识库' : 'Knowledge Base' });
                    } catch (err) {
                      console.error('[RagPanel] Failed to download RAG doc for preview:', err);
                    }
                  }}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText size={14} className="text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-700 truncate">{tool.name}</span>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); onToolRemove(tool.id); }}
                    className="p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-all">
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 隐藏的文件输入 */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileChange}
        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.svg,.webp,.xlsx,.xls,.pptx,.ppt,.csv,.txt,.md,.rtf,.mp4,.avi,.mov,.webm,.mp3,.wav,.flac,.aac,.ogg,.m4a,video/*,audio/*,image/*"
      />
    </div>
  );
};

export default RagToolsPanel;

